import {
  Avatar,
  Badge,
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Square,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import {
  AsyncSelect,
  chakraComponents,
  type OptionProps,
  type CSSObjectWithLabel,
} from 'chakra-react-select';
import { useMemo, useCallback, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { getUserFullName } from '@duke-hq/lib';
import { User as Consultant } from '@duke-hq/svc-api-db';
import { ProviderUserRoleColorSchemes } from '@duke-hq/shared-constants/providers';
import { useDebounceCallback } from 'usehooks-ts';
import { skipToken } from '@tanstack/react-query';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';

interface ReassignConsultantForm {
  tenant_id: number | null;
  consultant_id: string | null;
  site_location_id: string | null;
}

interface ConsultantOption extends Pick<Consultant, 'email' | 'picture_url' | 'role'> {
  value: string;
  label: string | undefined;
  fullname: string | undefined;
}

function ConsultantOptionComponent(props: OptionProps<ConsultantOption>) {
  return (
    <chakraComponents.Option {...props}>
      <Box display="flex" alignItems="center" justifyContent="space-between" w="full">
        <Box display="flex" alignItems="center">
          <Square size="32px">
            <Avatar
              size="sm"
              name={props.data.fullname}
              src={props.data.picture_url ?? undefined}
            />
          </Square>
          <VStack align="flex-start" gap={0} ml={4}>
            <Text fontWeight="medium">{props.data.label}</Text>
            <Text fontSize="sm" color={props.isSelected ? 'fg.inverse-subtle' : 'fg.muted'}>
              {props.data.email}
            </Text>
          </VStack>
        </Box>
        <Box display="flex" alignItems="flex-end">
          <Badge
            borderRadius="full"
            colorScheme={
              (props.data.role && ProviderUserRoleColorSchemes[props.data.role]) ?? 'gray'
            }
            variant="muted"
            textTransform="none"
          >
            {props.data.role?.replace(/_/g, ' ')}
          </Badge>
        </Box>
      </Box>
    </chakraComponents.Option>
  );
}

export function UpdateEnrolmentReassignFields({
  enrolmentId,
  currentValues,
  onClose,
  onSubmitExternal,
  onLoadingChange,
}: {
  enrolmentId: number;
  currentValues: {
    tenant_id?: number | null;
    consultant_id?: string | null;
    site_location_id?: string | null;
  };
  onClose: () => void;
  onSubmitExternal: (submitFn: () => void) => void;
  onLoadingChange: (isLoading: boolean) => void;
}) {
  const toast = useToast();
  const utils = trpcAdmin.useUtils();
  const reassignConsultant = trpcAdmin.enrolment.reassignConsultant.useMutation();

  const {
    control,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<ReassignConsultantForm>({
    defaultValues: {
      tenant_id: currentValues.tenant_id ?? null,
      consultant_id: currentValues.consultant_id ?? null,
      site_location_id: currentValues.site_location_id ?? null,
    },
  });

  const [tenantId, consultantId] = watch(['tenant_id', 'consultant_id']);

  // Fetch all providers
  const { data: providers } = trpcAdmin.providers.all.useQuery({}, { initialData: [] });

  // Fetch consultants from the selected tenant
  const { data: consultants, isFetching: isConsultantsLoading } =
    trpcAdmin.providers.user.listConsultants.useQuery(
      tenantId != null ? { providerId: tenantId } : skipToken,
      {
        enabled: tenantId != null,
      },
    );

  // Fetch sites accessible to the selected consultant within the provider
  const { data: sitesList, isFetching: isSitesLoading } =
    trpcAdmin.providers.students.listConsultantSiteAccess.useQuery(
      {
        providerId: tenantId ?? 0,
        consultantId: consultantId ?? '',
      },
      {
        enabled: tenantId != null && consultantId != null,
        initialData: [],
      },
    );

  const providerOptions = useMemo(() => {
    return providers
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((provider) => ({
        id: provider.id.toString(),
        value: provider.id,
        label: provider.name,
      }));
  }, [providers]);

  const loadProviderOptions = useDebounceCallback(
    (
      inputValue: string,
      callback: (options: { id: string; value: number; label: string }[]) => void,
    ) => {
      callback(
        providerOptions.filter((option) =>
          option.label.toLowerCase().includes(inputValue.toLowerCase()),
        ),
      );
    },
    500,
  );

  const consultantOptions: ConsultantOption[] = useMemo(() => {
    if (consultants) {
      return consultants.map((consultant) => ({
        role: consultant.role,
        email: consultant.email,
        picture_url: consultant.picture_url,
        value: consultant.id,
        label: getUserFullName(consultant),
        fullname: getUserFullName(consultant),
      }));
    }
    return [];
  }, [consultants]);

  const loadConsultantOptions = useDebounceCallback(
    (inputValue: string, callback: (options: ConsultantOption[]) => void) => {
      if (consultantOptions.length > 0) {
        callback(
          consultantOptions.filter(
            (option) =>
              (option.label != null &&
                option.label.toLowerCase().includes(inputValue.toLowerCase())) ||
              (option.email != null &&
                option.email.toLowerCase().includes(inputValue.toLowerCase())),
          ),
        );
      } else {
        callback([]);
      }
    },
    500,
  );

  const sitesOptions = useMemo(
    () =>
      sitesList.map((site) => ({
        value: site.id,
        label: site.state ? `${site.name}, ${site.state}` : site.name,
      })),
    [sitesList],
  );

  const loadSitesOptions = useDebounceCallback(
    (inputValue: string, callback: (options: { value: string; label: string }[]) => void) => {
      if (sitesOptions.length > 0) {
        callback(
          sitesOptions.filter(
            (option) =>
              option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
              option.value.toLowerCase().includes(inputValue.toLowerCase()),
          ),
        );
      } else {
        callback([]);
      }
    },
    500,
  );

  const handleProviderChange = useCallback(
    (selectedProviderId: number | null) => {
      if (selectedProviderId !== null) {
        setValue('tenant_id', selectedProviderId);
      }
      // New provider (or cleared provider) means new consultant and new site
      setValue('consultant_id', null);
      setValue('site_location_id', null);
    },
    [setValue],
  );

  const handleConsultantChange = useCallback(
    (selectedConsultantId: string | null) => {
      if (selectedConsultantId !== null) {
        setValue('consultant_id', selectedConsultantId);
      }
      // Different consultant (or cleared consultant) means we clear the site
      setValue('site_location_id', null);
    },
    [setValue],
  );

  const handleSiteChange = useCallback(
    (selectedSiteId: string | null) => {
      if (selectedSiteId !== null) {
        setValue('site_location_id', selectedSiteId);
      }
    },
    [setValue],
  );

  const onSubmit = handleSubmit(async (data) => {
    if (data.tenant_id === null || data.consultant_id === null || data.site_location_id === null) {
      return;
    }

    try {
      await reassignConsultant.mutateAsync({
        enrolment_id: enrolmentId,
        tenant_id: data.tenant_id,
        consultant_id: data.consultant_id,
        site_location_id: data.site_location_id,
      });

      toast({
        status: 'success',
        title: t`Consultant reassigned successfully`,
      });

      // Refetch enrolment data
      void utils.enrolment.byIdWithResourceStatus.invalidate({ enrolmentId });
      void utils.enrolment.list.invalidate(undefined, { refetchType: 'active' });

      onClose();
    } catch (error) {
      toast({
        status: 'error',
        title: error instanceof Error ? error.message : t`Failed to reassign consultant`,
      });
    }
  });

  // Expose submit function to parent
  useEffect(() => {
    onSubmitExternal(() => {
      void onSubmit();
    });
  }, [onSubmit, onSubmitExternal]);

  // Notify parent of loading state changes
  useEffect(() => {
    onLoadingChange(reassignConsultant.isPending);
  }, [reassignConsultant.isPending, onLoadingChange]);

  return (
    <VStack gap="16px" w="full" p="8px">
      <FormControl isInvalid={!!errors.tenant_id} isRequired>
        <FormLabel htmlFor="tenant_id">
          <Trans>Provider</Trans>
        </FormLabel>
        <Controller
          name="tenant_id"
          control={control}
          rules={{
            required: t`Provider is required.`,
          }}
          render={({ field }) => (
            <AsyncSelect
              id="tenant_id"
              placeholder={t`Select provider`}
              loadOptions={loadProviderOptions}
              defaultOptions={providerOptions}
              value={providerOptions.find((option) => option.value === field.value) ?? null}
              onChange={(val: { id: string; value: number; label: string } | null) => {
                field.onChange(val?.value);
                handleProviderChange(val?.value ?? null);
              }}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base: CSSObjectWithLabel) => ({
                  ...base,
                  zIndex: 9999,
                }),
              }}
            />
          )}
        />
        <FormErrorMessage>{errors.tenant_id?.message}</FormErrorMessage>
      </FormControl>

      <FormControl isInvalid={!!errors.consultant_id} isRequired>
        <FormLabel htmlFor="consultant_id">
          <Trans>Consultant</Trans>
        </FormLabel>
        <Controller
          name="consultant_id"
          control={control}
          rules={{
            required: t`Consultant is required.`,
          }}
          render={({ field }) => (
            <AsyncSelect
              id="consultant_id"
              placeholder={t`Select consultant`}
              loadOptions={loadConsultantOptions}
              defaultOptions={consultantOptions}
              value={consultantOptions.find((option) => option.value === field.value) ?? null}
              onChange={(val: ConsultantOption | null) => {
                field.onChange(val?.value);
                handleConsultantChange(val?.value ?? null);
              }}
              components={{ Option: ConsultantOptionComponent }}
              isDisabled={tenantId === null}
              isLoading={isConsultantsLoading}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base: CSSObjectWithLabel) => ({
                  ...base,
                  zIndex: 9999,
                }),
              }}
            />
          )}
        />
        <FormErrorMessage>{errors.consultant_id?.message}</FormErrorMessage>
      </FormControl>

      <FormControl isInvalid={!!errors.site_location_id} isRequired>
        <FormLabel htmlFor="site_location_id">
          <Trans>Site</Trans>
        </FormLabel>
        <Controller
          name="site_location_id"
          control={control}
          rules={{
            required: t`Site is required.`,
          }}
          render={({ field }) => (
            <AsyncSelect
              id="site_location_id"
              placeholder={t`Select site`}
              loadOptions={loadSitesOptions}
              defaultOptions={sitesOptions}
              value={sitesOptions.find((option) => option.value === field.value) ?? null}
              onChange={(val: { value: string; label: string } | null) => {
                field.onChange(val?.value);
                handleSiteChange(val?.value ?? null);
              }}
              isDisabled={tenantId === null}
              isLoading={isSitesLoading}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base: CSSObjectWithLabel) => ({
                  ...base,
                  zIndex: 9999,
                }),
              }}
            />
          )}
        />
        <FormErrorMessage>{errors.site_location_id?.message}</FormErrorMessage>
      </FormControl>
    </VStack>
  );
}
